/**
 * Toolbar components for the InputEditor
 *
 * This file contains all the toolbar UI components for the rich text editor:
 * - Individual formatting buttons (bold, italic, etc.)
 * - Menu-based dropdowns (alignment, lists, more options)
 * - Specialized buttons (undo/redo, links, indentation)
 */

import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSlate } from 'slate-react';
import { ReactEditor } from 'slate-react';
import {
  FormatBold,
  FormatItalic,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatIndentIncrease,
  FormatIndentDecrease,
  FormatListBulleted,
  FormatListNumbered,
  Undo,
  Redo,
  Link,
  LinkOff,
  FormatAlignCenter as AlignIcon,
  FormatListBulleted as ListIcon,
  MoreHoriz,
} from '@mui/icons-material';

import {
  isMarkActive,
  toggleMark,
  isBlockActive,
  toggleBlock,
  canIndent,
  canOutdent,
  increaseIndent,
  decreaseIndent,
  isLinkActive,
  unwrapLink,
  insertLink,
  isAlignType,
} from './input-editor.utils';
import { CustomElementFormat } from './input-editor.types';

// ============================================================================
// BASIC FORMATTING BUTTONS
// ============================================================================

/**
 * Generic button for text marks (bold, italic)
 */
export const MarkButton = ({
  format,
  icon: Icon,
}: {
  format: string;
  icon: React.ComponentType<any>;
}) => {
  const editor = useSlate();
  const isActive = isMarkActive(editor, format);

  return (
    <IconButton
      size="small"
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      sx={{
        color: isActive ? 'primary.main' : 'text.secondary',
        bgcolor: isActive ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );
};

/**
 * Generic button for block formatting (alignment, headings, etc.)
 */
export const BlockButton = ({
  format,
  icon: Icon,
}: {
  format: CustomElementFormat;
  icon: React.ComponentType<any>;
}) => {
  const editor = useSlate();
  const isActive = isBlockActive(
    editor,
    format,
    isAlignType(format) ? 'align' : 'type',
  );

  return (
    <IconButton
      size="small"
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      sx={{
        color: isActive ? 'primary.main' : 'text.secondary',
        bgcolor: isActive ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );
};

// ============================================================================
// SPECIALIZED BUTTONS
// ============================================================================

/**
 * Undo/Redo button with history state awareness
 */
export const UndoRedoButton = ({
  action,
  icon: Icon,
}: {
  action: 'undo' | 'redo';
  icon: React.ComponentType<any>;
}) => {
  const editor = useSlate() as any; // Cast to access history methods

  // Check if the action is available based on history state
  const canPerformAction =
    action === 'undo'
      ? editor.history?.undos?.length > 0
      : editor.history?.redos?.length > 0;

  return (
    <IconButton
      size="small"
      disabled={!canPerformAction}
      onMouseDown={(event) => {
        event.preventDefault();
        if (action === 'undo' && editor.undo) {
          editor.undo();
        } else if (action === 'redo' && editor.redo) {
          editor.redo();
        }
      }}
      sx={{
        color: canPerformAction ? 'text.secondary' : 'text.disabled',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );
};

/**
 * Link button with add/remove functionality
 * Shows both Link and LinkOff buttons when a link is active
 */
export const LinkButton = () => {
  const editor = useSlate();
  const isActive = isLinkActive(editor);

  /**
   * Handle link insertion by prompting user for URL
   */
  const handleInsertLink = () => {
    const url = window.prompt('Enter the URL:');
    if (!url) return;
    insertLink(editor, url);
  };

  return (
    <>
      <IconButton
        size="small"
        onMouseDown={(event) => {
          event.preventDefault();
          if (isActive) {
            unwrapLink(editor);
          } else {
            handleInsertLink();
          }
        }}
        sx={{
          color: isActive ? 'primary.main' : 'text.secondary',
          bgcolor: isActive ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Link fontSize="small" />
      </IconButton>
      {isActive && (
        <IconButton
          size="small"
          onMouseDown={(event) => {
            event.preventDefault();
            unwrapLink(editor);
          }}
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <LinkOff fontSize="small" />
        </IconButton>
      )}
    </>
  );
};

/**
 * Indentation button (increase/decrease)
 */
export const IndentButton = ({
  action,
  icon: Icon,
}: {
  action: 'increase' | 'decrease';
  icon: React.ComponentType<any>;
}) => {
  const editor = useSlate();
  const canPerformAction =
    action === 'increase' ? canIndent(editor) : canOutdent(editor);

  return (
    <IconButton
      size="small"
      disabled={!canPerformAction}
      onMouseDown={(event) => {
        event.preventDefault();
        ReactEditor.focus(editor);
        if (action === 'increase') {
          increaseIndent(editor);
        } else {
          decreaseIndent(editor);
        }
      }}
      sx={{
        color: canPerformAction ? 'text.secondary' : 'text.disabled',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );
};

// ============================================================================
// DROPDOWN MENU BUTTONS
// ============================================================================

/**
 * Text alignment dropdown menu
 * Provides options for left, center, right, and justify alignment
 */
export const AlignmentMenuButton = () => {
  const editor = useSlate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAlign = (format: string) => {
    toggleBlock(editor, format as CustomElementFormat);
    handleClose();
  };

  // Available alignment options
  const alignments = [
    { format: 'left', icon: FormatAlignLeft, label: 'Align Left' },
    { format: 'center', icon: FormatAlignCenter, label: 'Align Center' },
    { format: 'right', icon: FormatAlignRight, label: 'Align Right' },
    { format: 'justify', icon: FormatAlignJustify, label: 'Align Justify' },
  ];

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <AlignIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 150 } }}
      >
        {alignments.map(({ format, icon: Icon, label }) => (
          <MenuItem
            key={format}
            onClick={() => handleAlign(format)}
            sx={{
              bgcolor: isBlockActive(editor, format, 'align')
                ? 'action.selected'
                : 'transparent',
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * List formatting dropdown menu
 * Provides options for bulleted and numbered lists
 */
export const ListMenuButton = () => {
  const editor = useSlate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleList = (format: string) => {
    toggleBlock(editor, format as CustomElementFormat);
    handleClose();
  };

  // Available list options
  const lists = [
    { format: 'bulleted-list', icon: FormatListBulleted, label: 'Bullet List' },
    {
      format: 'numbered-list',
      icon: FormatListNumbered,
      label: 'Numbered List',
    },
  ];

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <ListIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 150 } }}
      >
        {lists.map(({ format, icon: Icon, label }) => (
          <MenuItem
            key={format}
            onClick={() => handleList(format)}
            sx={{
              bgcolor: isBlockActive(editor, format, 'type')
                ? 'action.selected'
                : 'transparent',
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * "More options" dropdown menu
 * Contains additional formatting options like indentation controls
 */
export const MoreMenuButton = () => {
  const editor = useSlate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleIndentIncrease = () => {
    increaseIndent(editor);
    handleClose();
  };

  const handleIndentDecrease = () => {
    decreaseIndent(editor);
    handleClose();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={handleIndentDecrease} disabled={!canOutdent(editor)}>
          <ListItemIcon>
            <FormatIndentDecrease fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Decrease Indent" />
        </MenuItem>
        <MenuItem onClick={handleIndentIncrease} disabled={!canIndent(editor)}>
          <ListItemIcon>
            <FormatIndentIncrease fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Increase Indent" />
        </MenuItem>
      </Menu>
    </>
  );
};
